/**
 * VoteStatus — horizontal stacked bar showing vote tallies.
 */
import React from 'react';
import type { VoteResult, VoteResolution } from '@/types/pm-types';

interface VoteStatusProps {
  result: VoteResult;
  totalEligible: number;
  resolution: VoteResolution;
  accentColor?: string;
}

export const VoteStatus: React.FC<VoteStatusProps> = ({
  result,
  totalEligible,
  resolution,
  accentColor,
}) => {
  const { approveCount, vetoCount, abstainCount } = result;
  const totalVoted = approveCount + vetoCount + abstainCount;
  const pending = Math.max(0, totalEligible - totalVoted);
  const total = Math.max(totalVoted + pending, 1);

  const approveWidth = (approveCount / total) * 100;
  const vetoWidth = (vetoCount / total) * 100;
  const pendingWidth = ((pending + abstainCount) / total) * 100;

  const progressText = (() => {
    switch (resolution.type) {
      case 'unanimous':
        return `${approveCount} of ${totalEligible} approved (need all ${totalEligible})`;
      case 'majority': {
        const needed = Math.floor(totalEligible / 2) + 1;
        return `${approveCount} of ${totalEligible} approved (need ${needed})`;
      }
      case 'quorum': {
        const target = resolution.quorumCount ??
          Math.ceil((resolution.quorumPercentage ?? 50) / 100 * totalEligible);
        return `${totalVoted} of ${totalEligible} voted (need ${target} to close)`;
      }
      case 'any':
        return `${totalVoted} votes cast`;
      default:
        return `${totalVoted} of ${totalEligible} voted`;
    }
  })();

  return (
    <div style={{ width: '100%' }}>
      {/* Stacked bar */}
      <div style={{
        display: 'flex',
        width: '100%',
        height: 6,
        borderRadius: 3,
        overflow: 'hidden',
        background: '#E5E7EB',
      }}>
        {approveWidth > 0 && (
          <div style={{
            width: `${approveWidth}%`,
            background: accentColor || '#22C55E',
            transition: 'width 0.3s ease',
          }} />
        )}
        {vetoWidth > 0 && (
          <div style={{
            width: `${vetoWidth}%`,
            background: '#EF4444',
            transition: 'width 0.3s ease',
          }} />
        )}
      </div>

      {/* Labels */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginTop: 4,
      }}>
        <span style={{ fontSize: 10, color: '#6B7280' }}>
          {approveCount > 0 && `${approveCount} approve`}
          {approveCount > 0 && vetoCount > 0 && ' · '}
          {vetoCount > 0 && `${vetoCount} veto`}
          {(approveCount > 0 || vetoCount > 0) && pending > 0 && ' · '}
          {pending > 0 && `${pending} pending`}
        </span>
        <span style={{ fontSize: 10, color: '#9CA3AF' }}>
          {progressText}
        </span>
      </div>
    </div>
  );
};

VoteStatus.displayName = 'VoteStatus';
